import { Alert, Button, Checkbox, Form, Input, InputNumber, message, Statistic, Table, Tag } from "antd";
import { collection, doc, onSnapshot, setDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { FinanceEntryType } from "../typeDefs";

const money = (value: number) => `$${(value || 0).toFixed(2)}`;

const liabilityCashOutSubCategories = [
  "GST Payment",
  "Income Tax Payment",
  "Provisional Tax Payment",
  "PAYE/Withholding Tax Payment",
  "Loan Repayment",
];

const liabilityCashInSubCategories = [
  "Loan Received",
  "Short-term Debt",
  "Long-term Debt",
  "Notes Payable",
  "Deferred Revenue",
];

const isTaxOrLiabilityPayment = (entry: FinanceEntryType) => {
  const searchable = `${entry.vendor || ""} ${entry.description || ""} ${entry.subCategory || ""}`.toLowerCase();
  return (
    liabilityCashOutSubCategories.includes(entry.subCategory) ||
    searchable.includes("ird") ||
    searchable.includes("inland revenue") ||
    searchable.includes("gst payment") ||
    searchable.includes("income tax") ||
    searchable.includes("provisional tax") ||
    searchable.includes("paye") ||
    searchable.includes("withholding tax") ||
    searchable.includes("loan repayment")
  );
};

const getCashImpact = (entry: FinanceEntryType) => {
  if (entry.apportionmentSource === "Personal Account") return 0;

  if (entry.category === "Revenue") return entry.amount;
  if (entry.category === "Expenses" || entry.category === "Assets") return -entry.amount;

  if (entry.category === "Equity") {
    if (entry.subCategory === "Shareholder Drawings") return -entry.amount;
    return entry.amount;
  }

  if (entry.category === "Liabilities") {
    if (isTaxOrLiabilityPayment(entry)) return -entry.amount;
    if (liabilityCashInSubCategories.includes(entry.subCategory)) return entry.amount;
    return 0;
  }

  return 0;
};

type BankReconciliation = {
  id: string;
  startDate: number;
  endDate: number;
  openingBankBalance: number;
  closingBankBalance: number;
  bankAdjustments: number;
  excludedEntryIds?: string[];
  notes?: string;
  updatedAt: number;
};

export const BankBalanceReconciliation = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const [form] = Form.useForm();
  const [saved, setSaved] = useState<BankReconciliation | null>(null);
  const [excludedEntryIds, setExcludedEntryIds] = useState<string[]>([]);

  const reconciliationId = `${dateRange[0]}_${dateRange[1]}`;

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "bankBalanceReconciliations"), (snapshot) => {
      const match = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() } as BankReconciliation))
        .find((item) => item.id === reconciliationId);

      setSaved(match || null);
      setExcludedEntryIds(match?.excludedEntryIds || []);
      if (match) form.setFieldsValue(match);
    });

    return unsub;
  }, [form, reconciliationId]);

  const entries = useMemo(
    () =>
      data
        .filter((item) => !item.isDeleted)
        .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]),
    [data, dateRange],
  );

  const includedEntries = entries.filter((item) => !excludedEntryIds.includes(item.id));
  const excludedEntries = entries.filter((item) => excludedEntryIds.includes(item.id));
  const cashMovement = includedEntries.reduce((total, item) => total + getCashImpact(item), 0);
  const excludedCashMovement = excludedEntries.reduce((total, item) => total + getCashImpact(item), 0);
  const importedBankTransactions = includedEntries.filter((item) => item.bankTransactionId).length;
  const unimportedEntries = includedEntries.filter((item) => !item.bankTransactionId);

  const values = Form.useWatch([], form) || {};
  const openingBankBalance = Number(values.openingBankBalance ?? saved?.openingBankBalance ?? 0);
  const closingBankBalance = Number(values.closingBankBalance ?? saved?.closingBankBalance ?? 0);
  const bankAdjustments = Number(values.bankAdjustments ?? saved?.bankAdjustments ?? 0);
  const expectedClosingBalance = openingBankBalance + cashMovement + bankAdjustments;
  const variance = closingBankBalance - expectedClosingBalance;

  const toggleExcluded = (entryId: string, excluded: boolean) => {
    setExcludedEntryIds((prev) =>
      excluded
        ? prev.includes(entryId)
          ? prev
          : [...prev, entryId]
        : prev.filter((id) => id !== entryId),
    );
  };

  const save = async () => {
    try {
      const formValues = await form.validateFields();
      await setDoc(doc(db, `bankBalanceReconciliations/${reconciliationId}`), {
        id: reconciliationId,
        startDate: dateRange[0],
        endDate: dateRange[1],
        openingBankBalance: Number(formValues.openingBankBalance || 0),
        closingBankBalance: Number(formValues.closingBankBalance || 0),
        bankAdjustments: Number(formValues.bankAdjustments || 0),
        excludedEntryIds,
        notes: formValues.notes || "",
        updatedAt: Date.now(),
      } as BankReconciliation);
      message.success("Bank balance reconciliation saved.");
    } catch (err) {
      console.error(err);
      message.error("Could not save bank reconciliation.");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Alert
        type={Math.abs(variance) < 0.01 ? "success" : "warning"}
        showIcon
        message={Math.abs(variance) < 0.01 ? "Bank balance reconciles" : "Bank balance variance found"}
        description="Enter your opening and financial year-end main bank account balance. Tax payments categorised as Liabilities / GST Payment, Income Tax Payment, Provisional Tax Payment, PAYE/Withholding Tax Payment, or Loan Repayment are treated as cash out. Loans received are treated as cash in. Exclude entries that did not affect this bank account."
      />

      <Form form={form} layout="vertical" initialValues={saved || { openingBankBalance: 0, closingBankBalance: 0, bankAdjustments: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          <Form.Item name="openingBankBalance" label="Opening bank balance">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="closingBankBalance" label="Financial year-end bank balance">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="bankAdjustments" label="Bank / timing adjustments">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Reconciliation notes">
          <Input.TextArea rows={3} placeholder="Explain any unreconciled amount, transfers, timing differences, or accountant adjustments." />
        </Form.Item>
        <Button type="primary" onClick={save}>Save bank reconciliation</Button>
      </Form>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16 }}>
        <Statistic title="Opening bank balance" value={openingBankBalance} precision={2} prefix="$" />
        <Statistic title="Included main-bank movement" value={cashMovement} precision={2} prefix="$" />
        <Statistic title="Excluded non-main-bank movement" value={excludedCashMovement} precision={2} prefix="$" />
        <Statistic title="Adjustments" value={bankAdjustments} precision={2} prefix="$" />
        <Statistic title="Expected closing balance" value={expectedClosingBalance} precision={2} prefix="$" />
        <Statistic title="Actual bank balance" value={closingBankBalance} precision={2} prefix="$" />
        <Statistic
          title="Variance"
          value={variance}
          precision={2}
          prefix="$"
          valueStyle={{ color: Math.abs(variance) < 0.01 ? "#3f8600" : "#cf1322" }}
        />
      </div>

      <Table
        title={() => `Main bank account inclusion (${includedEntries.length} included, ${excludedEntries.length} excluded)`}
        size="small"
        pagination={{ pageSize: 10 }}
        dataSource={entries.map((item) => ({ ...item, key: item.id }))}
        columns={[
          {
            title: "Include in main bank",
            render: (_value, record) => (
              <Checkbox
                checked={!excludedEntryIds.includes(record.id)}
                onChange={(event) => toggleExcluded(record.id, !event.target.checked)}
              />
            ),
          },
          { title: "Date", dataIndex: "date", render: (value) => new Date(value).toLocaleDateString("en-GB") },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Category", dataIndex: "category" },
          { title: "Subcategory", dataIndex: "subCategory" },
          { title: "Amount", dataIndex: "amount", render: money },
          { title: "Cash impact", render: (_value, record) => money(getCashImpact(record)) },
          {
            title: "Bank import",
            render: (_value, record) =>
              record.bankTransactionId ? <Tag color="green">Imported</Tag> : <Tag color="orange">Manual / no bank ID</Tag>,
          },
          {
            title: "Status",
            render: (_value, record) =>
              excludedEntryIds.includes(record.id) ? <Tag color="blue">Excluded</Tag> : <Tag color="green">Included</Tag>,
          },
        ]}
      />

      <Table
        title={() => `Included entries without bank import ID (${unimportedEntries.length})`}
        size="small"
        pagination={{ pageSize: 8 }}
        dataSource={unimportedEntries.map((item) => ({ ...item, key: item.id }))}
        columns={[
          { title: "Date", dataIndex: "date", render: (value) => new Date(value).toLocaleDateString("en-GB") },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Category", dataIndex: "category" },
          { title: "Subcategory", dataIndex: "subCategory" },
          { title: "Amount", dataIndex: "amount", render: money },
          { title: "Cash impact", render: (_value, record) => money(getCashImpact(record)) },
        ]}
      />

      <p style={{ margin: 0 }}>
        {entries.length} entries checked. {includedEntries.length} included in main bank reconciliation. {importedBankTransactions} included entries are linked to imported bank transactions.
      </p>
    </div>
  );
};
